import React from 'react';

import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import { Button } from '@/components/buttons/Button';
import Divider from './Divider';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { ONTARIO_REGISTRATION_CANADA_PROVINCES } from '@/constants/locales';
import {
  AddressRegistrationInfo,
  useAddressInfoData,
  useRegistrationActions,
} from '@/store/zustand/registration-store';
import { capitalize } from '@/utils/utils';
import Form from '@/components/form/Form';
import TextField from '@/components/form/text/TextField';
import SearchDropdownField from '@/components/form/search-dropdown/SearchDropdownField';
import { RegisterValidationRules } from '@/constants/constants';
import AddressField from '@/features/modal/modals/register/ontario/address-field/AddressField';

export const provinces = ONTARIO_REGISTRATION_CANADA_PROVINCES.map(province => ({
  value: province.code,
  label: province.name,
}));

const AddressInfoForm = ({
  stepBack,
  stepForward,
}: {
  stepBack: () => void;
  stepForward: () => void;
}) => {
  const { firstName, lastName, address, houseNumber, postCode, city, provinceCode } =
    useAddressInfoData();
  const { updateFields } = useRegistrationActions();
  const intl = useIntl();

  const initialValues: AddressRegistrationInfo = {
    firstName,
    lastName,
    address,
    houseNumber,
    postCode,
    city,
    provinceCode,
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required(intl.formatMessage({ id: 'input.error.required' }))
      .max(40, intl.formatMessage({ id: 'inputs.error.length.max' }, { length: 40 }))
      .min(2, intl.formatMessage({ id: 'inputs.error.length.min' }, { length: 2 }))
      .matches(
        RegisterValidationRules.firstNameAndLastName.AGCO,
        intl.formatMessage({ id: 'inputs.firstName.invalid' }),
      ),
    lastName: Yup.string()
      .required(intl.formatMessage({ id: 'input.error.required' }))
      .max(40, intl.formatMessage({ id: 'inputs.error.length.max' }, { length: 40 }))
      .min(2, intl.formatMessage({ id: 'inputs.error.length.min' }, { length: 2 }))
      .matches(
        RegisterValidationRules.firstNameAndLastName.AGCO,
        intl.formatMessage({ id: 'inputs.lastName.invalid' }),
      ),
    postCode: Yup.string()
      .required(intl.formatMessage({ id: 'input.error.required' }))
      .matches(
        /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
        intl.formatMessage({ id: 'inputs.postcode.invalid' }),
      ),
    address: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
    houseNumber: Yup.string()
      .matches(
        /^[1-9]\d*(?: ?(?:[A-Za-z]|[/-] ?\d+[A-Za-z]?))?$/,
        intl.formatMessage({ id: 'inputs.houseNumber.invalid' }),
      )
      .required(intl.formatMessage({ id: 'input.error.required' })),
    city: Yup.string()
      .matches(
        /^([a-zA-Z\u0080-\u024F]+(?:. |-| |'))*[a-zA-Z\u0080-\u024F]*$/,
        intl.formatMessage({ id: 'inputs.city.invalid' }),
      )
      .required(intl.formatMessage({ id: 'input.error.required' })),
    provinceCode: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
  });

  const handleOnSubmit = (values: AddressRegistrationInfo) => {
    triggerGTag(GTagEvents.ont_reg_step2_continue_cta_click);
    updateFields({
      ...values,
      firstName: capitalize(values.firstName),
      lastName: capitalize(values.lastName),
    });
    stepForward();
  };

  return (
    <div className="w-full h-full pt-5" data-cy="address_info_form">
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}
        className="flex flex-col justify-between gap-8 h-full"
      >
        {() => (
          <>
            <div className="flex flex-col gap-7">
              <div
                className="w-full flex flex-col gap-7 items-stretch sm:flex-row sm:items-center
                [&>div:first-of-type]:flex-[50%] [&>div:last-of-type]:flex-[50%]"
              >
                <TextField
                  data-cy="address_info_fname"
                  label={intl.formatMessage({ id: 'inputs.firstName' })}
                  name="firstName"
                  placeholder={intl.formatMessage({ id: 'registration.ontario.name.placeholder' })}
                  onValidationError={error =>
                    triggerGTag(GTagEvents.ont_reg_first_name_error, { error })
                  }
                  onFocus={() => triggerGTag(GTagEvents.ont_reg_first_name_click)}
                  className="border-[1px] border-white/30 text-white focus-within:border-blue-light
                  [&>input]:capitalize [&>input]:placeholder:normal-case"
                />
                <TextField
                  label={intl.formatMessage({ id: 'inputs.middleName' })}
                  name="middleName"
                  placeholder={`(${intl.formatMessage({ id: 'misc.optional' })})`}
                  className="border-[1px] border-white/30 text-white focus-within:border-blue-light
                  [&>input]:capitalize [&>input]:placeholder:normal-case"
                />
              </div>
              <TextField
                data-cy="address_info_lname"
                label={intl.formatMessage({ id: 'inputs.lastName' })}
                name="lastName"
                placeholder={intl.formatMessage({ id: 'registration.ontario.name.placeholder' })}
                onValidationError={error =>
                  triggerGTag(GTagEvents.ont_reg_last_name_error, { error })
                }
                onFocus={() => triggerGTag(GTagEvents.ont_reg_last_name_click)}
                className="border-[1px] border-white/30 text-white focus-within:border-blue-light
                [&>input]:capitalize [&>input]:placeholder:normal-case"
              />
              <AddressField />
              <Divider />
              <div
                className="w-full flex flex-col gap-7 items-stretch sm:flex-row sm:items-center
                [&>div:first-of-type]:flex-[50%] [&>div:last-of-type]:flex-[50%]"
              >
                <TextField
                  label={intl.formatMessage({ id: 'inputs.houseNumber' })}
                  data-cy="house_number"
                  name="houseNumber"
                  inputMode="numeric"
                  onValidationError={error =>
                    triggerGTag(GTagEvents.ont_reg_number_error, { error })
                  }
                  onFocus={() => triggerGTag(GTagEvents.ont_reg_number_click)}
                  className="border-[1px] border-white/30 text-white focus-within:border-blue-light"
                />
                <TextField
                  data-cy="address_info_zip"
                  label={intl.formatMessage({ id: 'inputs.postcode' })}
                  name="postCode"
                  onValidationError={error =>
                    triggerGTag(GTagEvents.ont_reg_zipcode_error, { error })
                  }
                  onFocus={() => triggerGTag(GTagEvents.ont_reg_zipcode_click)}
                  className="border-[1px] border-white/30 text-white focus-within:border-blue-light"
                />
              </div>
              <TextField
                label={intl.formatMessage({ id: 'inputs.city' })}
                name="city"
                onValidationError={error => triggerGTag(GTagEvents.ont_reg_city_error, { error })}
                onFocus={() => triggerGTag(GTagEvents.ont_reg_city_click)}
                className="border-[1px] border-white/30 text-white focus-within:border-blue-light"
              />

              <SearchDropdownField
                name="provinceCode"
                label={intl.formatMessage({ id: 'inputs.province' })}
                options={provinces}
                onValidationError={error =>
                  triggerGTag(GTagEvents.ont_reg_province_error, { error })
                }
                onFocus={() => triggerGTag(GTagEvents.ont_reg_province_click)}
                labelClassName="text-white"
                inputClassName="!bg-transparent border-[1px] border-white/30 text-white focus-within:border-blue-light"
              />
            </div>
            <div className="flex flex-col-reverse items-center gap-4 mt-4 sm:flex-row">
              <Button
                text={intl.formatMessage({ id: 'misc.back' })}
                onClickHandler={() => {
                  triggerGTag(GTagEvents.ont_reg_step2_back_cta_click);
                  stepBack();
                }}
                btnType="secondary"
              />
              <Button
                type="submit"
                text={intl.formatMessage({ id: 'misc.continue' })}
                btnType="primary"
              />
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

export default AddressInfoForm;
